import { Flex, Grid, Tag, Typography } from "antd/lib";
import styles from "./Events.module.scss";
import React from "react";
import { useTranslation } from "react-i18next";
import Link from "next/link";
import { Event } from "@/types/events";
import { myEventDetailsUrl, myEventMaterialsUrl, publicEventDetailsUrl, reportMyEventUrl } from "@/utils/appUrls";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useAuth } from "@/context/AuthContext";

const { Text, Title } = Typography;

interface EventsCardProps {
  isMyEvents?: boolean;
  isReport?: boolean;
  materialsListButton?: boolean;
  event: Event;
  detailsBtnRef?: React.MutableRefObject<any>;
}

const { useBreakpoint } = Grid;

dayjs.extend(utc);

const EventCard: React.FC<EventsCardProps> = ({
  isMyEvents,
  isReport,
  materialsListButton = false,
  event,
  detailsBtnRef,
}) => {
  const { t } = useTranslation();
  const viewDetailsLink = isMyEvents ? myEventDetailsUrl(event.uuid) : publicEventDetailsUrl(event.uuid);
  const viewEventMaterials = myEventMaterialsUrl(event.uuid);
  const screens = useBreakpoint();
  const { userIsRetoolingFacilitator } = useAuth();

  return (
    <Flex vertical justify="space-between" gap={25} className={styles.eventCard}>
      <Flex justify="space-between" gap={16} vertical={!screens.sm}>
        <Flex vertical justify="space-between" gap={16}>
          <Text>
            {dayjs.utc(event.start_date).format("D MMMM YYYY")} - {dayjs.utc(event.end_date).format("D MMMM YYYY")}
          </Text>
          {!!event?.schedule_tips && <Text>{event.schedule_tips}</Text>}
          {!!event.title && (
            <Title level={5} style={{ margin: 0 }}>
              {event.title}
            </Title>
          )}
        </Flex>
        <Flex vertical justify="space-between" align={screens.sm ? "flex-end" : "flex-start"}>
          <Flex vertical gap={8} align={screens.sm ? "flex-end" : "flex-start"}>
            {!!event.event_type && (
              <Tag bordered={false} color="cyan">
                {/*@ts-ignore*/}
                {t(`eventsPage.${event.event_type.toLowerCase()}`)}
              </Tag>
            )}
            <Tag bordered={false} color="green">
              {event.online ? t("eventsPage.tags.online") : t("eventsPage.tags.inPerson")}
            </Tag>
            {isMyEvents && (
              <Tag bordered={false} color="green">
                {event.public ? t("eventsPage.tags.public") : t("eventsPage.tags.private")}
              </Tag>
            )}
          </Flex>
        </Flex>
      </Flex>
      <Flex justify="space-between" align={screens.sm ? "center" : "stretch"} gap={16} vertical={!screens.sm}>
        <Flex gap={screens.sm ? 48 : 16} style={{ display: "flex" }} vertical={!screens.sm}>
          {!!event.location_place && (
            <Flex gap={8} align="center">
              <Text style={{ borderRadius: "50%", backgroundColor: "#D9D9D9" }}>
                <i className={`${styles.cardIcon} icon-world_stroke`} />
              </Text>
              <Text strong>{event.location_place}</Text>
            </Flex>
          )}
          {isMyEvents && !event.has_confidential_participants && (
            <Flex gap={8} align="center">
              <Text style={{ borderRadius: "50%", backgroundColor: "#D9D9D9" }}>
                <i className={`${styles.cardIcon} icon-user_stroke`} />
              </Text>
              <Flex gap={4}>
                <Text>People: </Text>
                <Text strong>{event.participants_count}</Text>
              </Flex>
            </Flex>
          )}
        </Flex>
        <Flex gap={16}>
          {materialsListButton && (
            <Link href={viewEventMaterials} className="buttonLink buttonLink_secondary">
              {t("myProgramsPage.buttons.viewMaterials")}
            </Link>
          )}
          {isReport && !event.reported && !userIsRetoolingFacilitator ? (
            <Link
              href={reportMyEventUrl(event.uuid)}
              className={`buttonLink buttonLink_secondary ${screens.sm ? "" : "w-full"}`}
            >
              {t("myReportingPage.buttons.createReport")}
            </Link>
          ) : event.is_report ? (
            <></>
          ) : (
            <Link
              href={viewDetailsLink}
              ref={detailsBtnRef}
              className={`buttonLink buttonLink_secondary ${screens.sm || materialsListButton ? "" : "w-full"}`}
            >
              {isReport ? t("myReportingPage.buttons.viewEvent") : t("eventsPage.buttons.viewDetails")}
            </Link>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

export default EventCard;
